import { unref } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useGroupsUIStore } from '@communities/groups-layer/stores/groups.store'

import { storeToRefs } from 'pinia'
import { GokollabInvitationsService } from '../services/gokollab-invitations.service'
import { InvitationsService } from '../services/invitations.service'

import { useInvitationsStore } from '../stores/invitations.store'
import { InvitationType, InvitationUserRole } from '../types/invitation'

import type { IInvitationDetails } from '../types/invitation'

/**
 * useInvitations — orchestrates all invitation flows:
 *   - Standard flow: creates invitation records via COMMUNITIES_SERVER_URL
 *   - Gokollab (affiliate) flow: generates referral links via GOKOLLAB_SERVER_URL
 *
 * Follows the composable pattern: facade over store + services with no API calls in the store.
 * Component → useInvitations → store (state) + services (API)
 */
export const useInvitations = () => {
  const store = useInvitationsStore()
  const groupsStore = useGroupsUIStore()
  const invitationsService = new InvitationsService()
  const gokollabService = new GokollabInvitationsService()
  const { isGokollab } = useGlobal()
  const { store: authStore } = useAuth()

  // ===== STATE =====
  const { invitationLink, forbiddenInvitation, showInvitationModal, isGeneratingLink } = storeToRefs(store)

  // ===== HELPERS =====
  function buildGroupUrl(groupSlug: string): string {
    // Gokollab routes groups at the root (`/<slug>/home`); the communities host
    // uses the `/communities/groups/<slug>/home` prefix. Mirrors legacy
    // `GroupState.state.groupLink` in ghl-revex-frontend.
    const path = unref(isGokollab) ? `/${groupSlug}/home` : `/communities/groups/${groupSlug}/home`
    if (process.client) {
      return `${window.location.origin}${path}`
    }
    return path
  }

  // ===== ACTIONS =====

  /**
   * Generates an invitation link for the current group.
   * Mirrors the legacy `ghl-revex-frontend` flow:
   * - Gokollab: tries affiliate generate-link; on ANY failure (incl. 403) it
   *   silently falls back to the plain group URL — matches the legacy
   *   `handleGokollabInvitation` try/catch that swallows errors. Users in
   *   Gokollab mode always get a usable invite UI and the email-invite flow
   *   (generateLead) is independent of this endpoint.
   * - Standard: creates an INVITATION_TYPE.LINK record → `<groupUrl>?invite=<id>`.
   *   On 403 → sets forbiddenInvitation = true so the modal shows the
   *   permission-denied state.
   */
  async function createInvitationLink(): Promise<void> {
    const group = groupsStore.currentGroup
    if (!group?._id || !group?.locationId) return

    store.setIsGeneratingLink(true)
    store.setForbiddenInvitation(false)

    try {
      if (unref(isGokollab)) {
        const groupUrl = buildGroupUrl(group.slug)
        let link = groupUrl
        try {
          const response = await gokollabService.generateInvitationLink(group.locationId, group._id)
          link = response?.referralLink || groupUrl
        } catch (gokollabError) {
          // Legacy app silently swallows errors here (console.error only) and
          // falls back to the group URL — do the same so non-affiliate users
          // (e.g. owners) can still share a link + send email invites.
          console.error('[useInvitations] gokollab generate-link failed, falling back to group URL', gokollabError)
        }
        store.setGokollabInvitationLink(link)
        store.setInvitationLink(link)
      } else {
        const invitation = await invitationsService.create(
          group._id,
          {
            locationId: group.locationId,
            groupId: group._id,
            invitationType: InvitationType.LINK,
            createdBy: authStore.contactId || '',
          },
          group.locationId
        )
        const groupUrl = buildGroupUrl(group.slug)
        store.setInvitationLink(`${groupUrl}?invite=${invitation._id}`)
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError?.response?.status === 403) {
        store.setForbiddenInvitation(true)
      }
    } finally {
      store.setIsGeneratingLink(false)
    }
  }

  /**
   * Sends an email invitation to a specific person by creating an
   * INVITATION_TYPE.EMAIL record. Used in both standard and Gokollab modes.
   */
  async function sendEmailInvitation(details: IInvitationDetails): Promise<void> {
    const group = groupsStore.currentGroup
    if (!group?._id || !group?.locationId) throw new Error('No active group')

    await invitationsService.create(
      group._id,
      {
        locationId: group.locationId,
        groupId: group._id,
        invitationType: InvitationType.EMAIL,
        createdBy: authStore.contactId || '',
        invitationDetails: details,
      },
      group.locationId
    )
  }

  function openInvitationModal(): void {
    store.setShowInvitationModal(true)
  }

  function closeInvitationModal(): void {
    store.setShowInvitationModal(false)
    store.resetInvitationState()
  }

  /**
   * Accepts an invitation by ID. Returns the `magicLink` URL from the server
   * (present when the invitation grants access via a redirect) or `null` when
   * no redirect is required / the call failed. Mirrors the legacy
   * `InvitationState.acceptInvitation` call site in `GroupContainer.vue`.
   */
  async function acceptInvitation(invitationId: string): Promise<string | null> {
    const group = groupsStore.currentGroup
    if (!group?._id || !group?.locationId || !invitationId) return null

    try {
      // Backend returns `{ magicLink }` on success, or bare `false` when
      // add-to-group fails after a 200 (see communities InvitationsService.acceptInvitations).
      const response = (await invitationsService.acceptInvitation(group._id, invitationId, group.locationId)) as
        | { magicLink?: string }
        | false
        | undefined

      if (response === false) {
        throw new Error('INVITATION_ACCEPT_FAILED')
      }

      return response?.magicLink || null
    } catch (error) {
      // Surface the original axios error so the caller can decide how to
      // notify the user (to match the legacy `notification.create(...)` path).
      throw error
    }
  }

  return {
    // State
    invitationLink,
    forbiddenInvitation,
    showInvitationModal,
    isGeneratingLink,
    store,
    // Actions
    createInvitationLink,
    sendEmailInvitation,
    openInvitationModal,
    closeInvitationModal,
    acceptInvitation,
    // Enums (re-exported for use in templates)
    InvitationUserRole,
  }
}
